import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Select, Store } from '@ngxs/store';
import {
  EditMessage,
  LoadChatQueue,
  LoadChats,
  LoadMessageTemplates,
  LoadNotificationChannels,
  MessagesState,
  OpenChat,
  SaveNotificationChannels,
} from '@vandelft/modules/shared/state/messages';
import { firstValueFrom, Observable, Subscription } from 'rxjs';
import { Chat, Message, MessageTemplate } from '@vandelft/modules/shared/models';
import { AuthState } from '@vandelft/modules/shared/state/auth';
import { NOTIFICATION_CHANNEL_OPTIONS } from '@vandelft/modules/shared/constants';
import { NotificationChannel } from '@vandelft/shared/interfaces';

const PREVIEW_LENGTH = 200;

@Component({
    selector: 'app-message-overview',
    styleUrls: ['./message-overview.component.scss'],
    templateUrl: './message-overview.component.html',
    standalone: false
})
export class MessageOverviewComponent implements OnInit, OnDestroy {
  @Select(MessagesState.chats) public allChats$: Observable<Chat[]>;
  @Select(MessagesState.chatQueue) public chatQueue$: Observable<Chat[]>;
  @Select(MessagesState.templates) public templates$: Observable<MessageTemplate[]>;
  @Select(MessagesState.notificationChannels) public notificationChannels$: Observable<NotificationChannel[]>;
  @Select(AuthState.isAdmin) public isAdmin$: Observable<boolean>;

  public allChatsPage = 0;
  public chatsQueuePage = 0;
  public isSavingChannels = false;

  public channelOptions = NOTIFICATION_CHANNEL_OPTIONS;

  public channelsForm: FormGroup = new FormGroup({
    email: new FormControl(false),
    whatsapp: new FormControl(false),
  });

  private subscriptions: Subscription[] = [];

  public constructor(private store: Store) {}

  public ngOnInit(): void {
    this.store.dispatch(new LoadChats());
    this.store.dispatch(new LoadChatQueue());
    this.store.dispatch(new LoadMessageTemplates());
    this.store.dispatch(new LoadNotificationChannels());

    this.subscriptions.push(
      this.notificationChannels$.subscribe((channels: NotificationChannel[]): void => {
        this.channelsForm.patchValue({
          email: !!channels?.includes('email'),
          whatsapp: !!channels?.includes('whatsapp'),
        });
      }),
    );
  }

  public openChat(id: string): void {
    if (!id) {
      return;
    }

    this.store.dispatch(new OpenChat(id));
  }

  public editMessage(event: Event, message: Message): void {
    event.preventDefault();
    this.store.dispatch(new EditMessage(message));
  }

  public getTemplate(message: Message, templates: MessageTemplate[]): MessageTemplate {
    return templates?.find((template: MessageTemplate): boolean => template.name === message?.template);
  }

  public getChannelLabel(channel: NotificationChannel): string {
    return this.channelOptions.find((option): boolean => option.value === channel)?.label ?? channel;
  }

  /**
   * Email rows have no body and their template is a backend Handlebars view, so it never matches the
   * Meta templates `/whatsapp/templates` returns. Falling through to a label keeps those rows
   * readable instead of rendering the template lookup's `undefined`.
   */
  public getPreview(message: Message, templates: MessageTemplate[]): string {
    const body: string = message?.message ?? this.getTemplate(message, templates)?.bodyText ?? '';

    if (!body) {
      return message?.channel === 'email' ? 'Standaard reviewverzoek' : (message?.template ?? '');
    }

    return body.length > PREVIEW_LENGTH ? `${body.substring(0, PREVIEW_LENGTH)}...` : body;
  }

  public async saveChannels(): Promise<void> {
    if (this.isSavingChannels) {
      return;
    }

    const channels: NotificationChannel[] = this.channelOptions
      .filter((option): boolean => !!this.channelsForm.get(option.value)?.value)
      .map((option): NotificationChannel => option.value as NotificationChannel);

    try {
      this.isSavingChannels = true;
      await firstValueFrom(this.store.dispatch(new SaveNotificationChannels(channels)));
    } catch (e) {
      // The interceptor already showed the server's message; the form keeps the operator's choice.
    } finally {
      this.isSavingChannels = false;
    }
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription): void => subscription.unsubscribe());
  }
}
